import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Plus,
  Edit,
  Trash2,
  ClipboardList,
  Check,
  X,
  ChevronRight,
  MessageSquare,
  Phone,
  Smile,
  Activity,
  ChevronDown
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";

const MatricesPage = () => {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("templates"); // templates or agent_performance
  
  // Agent Performance states
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [channel, setChannel] = useState("calls"); // calls or chats
  const [performanceData, setPerformanceData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);

  const fetchAgents = async () => {
    try {
      const res = await authAxios.get("/agents");
      setAgents(res.data);
      if (res.data.length > 0 && !selectedAgent) {
        setSelectedAgent(res.data[0]);
      }
    } catch (e) {
      console.error("Error fetching agents:", e);
    }
  };

  const fetchPerformance = async () => {
    if (!selectedAgent) return;
    try {
      setPerfLoading(true);
      const res = await authAxios.get(`/qa/agent-performance?agent=${encodeURIComponent(selectedAgent)}&channel=${channel}`);
      setPerformanceData(res.data);
    } catch (e) {
      console.error("Error fetching performance:", e);
      toast.error("Error al cargar el desempeño");
    } finally {
      setPerfLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (activeTab === "agent_performance" && selectedAgent) {
      fetchPerformance();
    }
  }, [selectedAgent, channel, activeTab]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMatrix, setEditingMatrix] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    criteria: [],
  });
  const [newCriterion, setNewCriterion] = useState({
    name: "",
    description: "",
    weight: 10,
    options: [],
  });

  const fetchMatrices = async () => {
    try {
      const response = await authAxios.get("/matrices");
      setMatrices(response.data);
    } catch (error) {
      console.error("Error fetching matrices:", error);
      toast.error("Error al cargar matrices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatrices();
  }, []);

  const resetForm = () => {
    setFormData({ name: "", description: "", criteria: [] });
    setNewCriterion({ name: "", description: "", weight: 10, options: [] });
    setEditingMatrix(null);
  };

  const handleOpenDialog = (matrix = null) => {
    if (matrix) {
      setEditingMatrix(matrix);
      setFormData({
        name: matrix.name,
        description: matrix.description,
        criteria: matrix.criteria || [],
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const addCriterion = () => {
    if (!newCriterion.name) {
      toast.error("El criterio debe tener un nombre");
      return;
    }
    setFormData({
      ...formData,
      criteria: [...formData.criteria, { ...newCriterion, id: Date.now().toString() }],
    });
    setNewCriterion({ name: "", description: "", weight: 10, options: [] });
  };

  const removeCriterion = (index) => {
    setFormData({
      ...formData,
      criteria: formData.criteria.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      if (editingMatrix) {
        await authAxios.put(`/matrices/${editingMatrix.id}`, formData);
        toast.success("Matriz actualizada");
      } else {
        await authAxios.post("/matrices", formData);
        toast.success("Matriz creada");
      }
      handleCloseDialog();
      fetchMatrices();
    } catch (error) {
      toast.error("Error al guardar la matriz");
    }
  };

  const handleDelete = async (matrixId) => {
    try {
      await authAxios.delete(`/matrices/${matrixId}`);
      toast.success("Matriz eliminada");
      fetchMatrices();
    } catch (error) {
      toast.error("Error al eliminar la matriz");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Matriz y Desempeño de Calidad</h1>
          <p className="text-slate-500 mt-1">
            Define matrices de evaluación y visualiza el perfil de calidad por agente.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 w-fit shrink-0">
          <Button
            variant={activeTab === "templates" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("templates")}
            className="text-xs h-7 px-3 font-semibold"
          >
            Plantillas de Matriz
          </Button>
          <Button
            variant={activeTab === "agent_performance" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("agent_performance")}
            className="text-xs h-7 px-3 font-semibold"
          >
            Desempeño por Agente
          </Button>
        </div>
      </div>

      {activeTab === "agent_performance" ? (
        <div className="space-y-6">
          {/* Agent selection header */}
          <Card>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Label htmlFor="agent-select" className="font-semibold text-slate-700">Seleccionar Agente:</Label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger className="w-[200px]" id="agent-select">
                    <SelectValue placeholder="Elegir agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((agent) => (
                      <SelectItem key={agent} value={agent}>{agent}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                <button
                  onClick={() => setChannel("calls")}
                  className={`px-4 py-1 text-xs font-semibold rounded-md transition-all ${
                    channel === "calls" ? "bg-white text-sky-600 shadow-xs" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Llamadas
                </button>
                <button
                  onClick={() => setChannel("chats")}
                  className={`px-4 py-1 text-xs font-semibold rounded-md transition-all ${
                    channel === "chats" ? "bg-white text-emerald-600 shadow-xs" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Chats (Mensajes)
                </button>
              </div>
            </CardContent>
          </Card>

          {perfLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            </div>
          ) : (
            <>
              {/* KPI Cards Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="border-l-4 border-l-sky-500">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase">Calidad Promedio</p>
                      <p className="text-2xl font-bold mt-1 text-sky-600">
                        {performanceData?.avg_score !== null && performanceData?.avg_score !== undefined
                          ? `${performanceData.avg_score}%`
                          : (channel === "calls" ? "N/A" : `${((performanceData?.avg_sentiment || 5.0) * 10).toFixed(0)}%`)}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-sky-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase">Interacciones Totales</p>
                      <p className="text-2xl font-bold mt-1 text-emerald-600">
                        {performanceData?.total_interactions || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-emerald-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase">Promedio Interacción</p>
                      <p className="text-2xl font-bold mt-1 text-purple-600">
                        {channel === "calls"
                          ? `${Math.floor((performanceData?.avg_interaction || 0) / 60)}m ${Math.round((performanceData?.avg_interaction || 0) % 60)}s`
                          : `${performanceData?.avg_interaction || 0} msgs`}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      {channel === "calls" ? <Phone className="w-5 h-5 text-purple-600" /> : <MessageSquare className="w-5 h-5 text-purple-600" />}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase">Sentimiento Promedio</p>
                      <p className="text-2xl font-bold mt-1 text-orange-600">
                        {performanceData?.avg_sentiment || "5.0"}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Smile className="w-5 h-5 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Chart & Table */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Evolution Chart */}
                <Card className="lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-md font-semibold text-slate-800">Evolución de Calidad</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] w-full">
                      {(!performanceData?.history || performanceData.history.length === 0) ? (
                        <p className="text-sm text-slate-500 text-center py-16">Sin datos históricos suficientes.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={[...performanceData.history].reverse()}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey={channel === "calls" ? "score" : "sentiment"}
                              stroke={channel === "calls" ? "#0EA5E9" : "#10B981"}
                              strokeWidth={2}
                              name={channel === "calls" ? "Calificación" : "Sentimiento"}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* History Table */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-md font-semibold text-slate-800">Historial de Evaluaciones y Feedback</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                      <table className="w-full text-left border-collapse text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-100">
                            <th className="px-4 py-3 text-slate-500 font-semibold">Fecha</th>
                            <th className="px-4 py-3 text-slate-500 font-semibold">{channel === "calls" ? "Nota" : "Sentimiento"}</th>
                            <th className="px-4 py-3 text-slate-500 font-semibold">Evaluador</th>
                            <th className="px-4 py-3 text-slate-500 font-semibold">Comentarios del Supervisor</th>
                            <th className="px-4 py-3 text-right">Detalle</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(!performanceData?.history || performanceData.history.length === 0) ? (
                            <tr>
                              <td colSpan={5} className="text-center py-8 text-slate-400">No hay interacciones recientes.</td>
                            </tr>
                          ) : (
                            performanceData.history.map((row, idx) => (
                              <tr key={idx} className="hover:bg-slate-50/50">
                                <td className="px-4 py-3 text-slate-500">{row.date}</td>
                                <td className="px-4 py-3 font-semibold">
                                  {channel === "calls" ? (
                                    row.score !== null ? `${row.score}/100` : "Pendiente"
                                  ) : (
                                    `${row.sentiment}`
                                  )}
                                </td>
                                <td className="px-4 py-3 text-slate-600">{row.evaluator}</td>
                                <td className="px-4 py-3 text-slate-600 truncate max-w-xs">{row.comments}</td>
                                <td className="px-4 py-3 text-right">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(channel === "chats" ? `/chats/${row.id}` : `/calls/${row.id}`)}
                                    className="text-sky-600 hover:text-sky-800"
                                  >
                                    Ver
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Plantillas de Matriz</h2>
              <p className="text-slate-500 mt-1">Define los criterios para evaluar la calidad de las llamadas</p>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-sky-600 hover:bg-sky-700"
                  onClick={() => handleOpenDialog()}
                  data-testid="create-matrix-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Matriz
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMatrix ? "Editar Matriz" : "Nueva Matriz de Evaluación"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre de la Matriz</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Evaluación Servicio al Cliente"
                    data-testid="matrix-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción de la matriz..."
                    rows={3}
                    data-testid="matrix-description-input"
                  />
                </div>
              </div>

              {/* Criteria Section */}
              <div className="space-y-4">
                <Label>Criterios de Evaluación</Label>
                
                {formData.criteria.length > 0 && (
                  <div className="space-y-2">
                    {formData.criteria.map((criterion, index) => (
                      <div
                        key={criterion.id || index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{criterion.name}</p>
                          {criterion.description && (
                            <p className="text-sm text-slate-500">{criterion.description}</p>
                          )}
                          <Badge variant="outline" className="mt-1">
                            Peso: {criterion.weight}%
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeCriterion(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new criterion */}
                <div className="p-4 border border-dashed border-slate-300 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-slate-600">Agregar Criterio</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Nombre del criterio"
                      value={newCriterion.name}
                      onChange={(e) => setNewCriterion({ ...newCriterion, name: e.target.value })}
                      data-testid="criterion-name-input"
                    />
                    <Input
                      type="number"
                      placeholder="Peso (%)"
                      value={newCriterion.weight}
                      onChange={(e) => setNewCriterion({ ...newCriterion, weight: parseInt(e.target.value) || 0 })}
                      min={0}
                      max={100}
                      data-testid="criterion-weight-input"
                    />
                  </div>
                  <Input
                    placeholder="Descripción del criterio"
                    value={newCriterion.description}
                    onChange={(e) => setNewCriterion({ ...newCriterion, description: e.target.value })}
                    data-testid="criterion-description-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCriterion}
                    className="w-full"
                    data-testid="add-criterion-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Criterio
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="save-matrix-btn">
                  {editingMatrix ? "Guardar Cambios" : "Crear Matriz"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Matrices Grid */}
      {matrices.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No hay matrices creadas</h3>
            <p className="text-slate-500 mb-4">
              Crea tu primera matriz de evaluación para comenzar a analizar llamadas
            </p>
            <Button onClick={() => handleOpenDialog()} className="bg-sky-600 hover:bg-sky-700">
              <Plus className="w-4 h-4 mr-2" />
              Crear Matriz
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {matrices.map((matrix) => (
            <Card
              key={matrix.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`matrix-card-${matrix.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold line-clamp-1">
                      {matrix.name}
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                      {matrix.description || "Sin descripción"}
                    </p>
                  </div>
                  {matrix.is_active && (
                    <Badge className="bg-green-100 text-green-700 ml-2">Activa</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Criterios</span>
                    <span className="font-medium">{matrix.criteria?.length || 0}</span>
                  </div>
                  
                  {matrix.criteria && matrix.criteria.length > 0 && (
                    <div className="space-y-1">
                      {matrix.criteria.slice(0, 3).map((criterion, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between text-sm bg-slate-50 px-2 py-1 rounded"
                        >
                          <span className="text-slate-600 truncate">{criterion.name}</span>
                          <Badge variant="outline" className="text-xs ml-2">
                            {criterion.weight}%
                          </Badge>
                        </div>
                      ))}
                      {matrix.criteria.length > 3 && (
                        <p className="text-xs text-slate-400 text-center">
                          +{matrix.criteria.length - 3} más
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-xs text-slate-400">
                      {matrix.created_at ? format(new Date(matrix.created_at), "dd MMM yyyy", { locale: es }) : "-"}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(matrix)}
                        data-testid={`edit-matrix-${matrix.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      {user?.role === "admin" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-matrix-${matrix.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar matriz?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. La matriz "{matrix.name}" será eliminada permanentemente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(matrix.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default MatricesPage;
